<template>
  <div class="sms-task-list">
    <el-card class="box-card">
      <div slot="header" class="clearfix">
        <span>短信任务列表</span>
        <el-button style="float: right; padding: 3px 0" type="text" @click="refreshTaskList">
          刷新
        </el-button>
      </div>
      
      <div class="task-statistics">
        <el-row :gutter="20">
          <el-col :span="6">
            <div class="stat-item">
              <div class="stat-label">总任务数</div>
              <div class="stat-value">{{ smsStatistics.total }}</div>
            </div>
          </el-col>
          <el-col :span="6">
            <div class="stat-item">
              <div class="stat-label">待处理</div>
              <div class="stat-value">{{ smsStatistics.pending }}</div>
            </div>
          </el-col>
          <el-col :span="6">
            <div class="stat-item">
              <div class="stat-label">处理中</div>
              <div class="stat-value">{{ smsStatistics.processing }}</div>
            </div>
          </el-col>
          <el-col :span="6">
            <div class="stat-item">
              <div class="stat-label">已完成</div>
              <div class="stat-value">{{ smsStatistics.completed }}</div>
            </div>
          </el-col>
        </el-row>
      </div>

      <el-table
        v-loading="smsTaskLoading"
        :data="smsTaskList"
        style="width: 100%"
      >
        <el-table-column
          prop="taskId"
          label="任务ID"
          width="180"
        />
        <el-table-column
          prop="createdAt"
          label="创建时间"
          width="180"
        >
          <template slot-scope="scope">
            {{ formatDateTime(scope.row.createdAt) }}
          </template>
        </el-table-column>
        <el-table-column
          prop="totalCount"
          label="总数量"
          width="100"
        />
        <el-table-column
          prop="successCount"
          label="成功数"
          width="100"
        />
        <el-table-column
          prop="failCount"
          label="失败数"
          width="100"
        />
        <el-table-column
          prop="taskStatus"
          label="状态"
        >
          <template slot-scope="scope">
            <el-tag :type="getTaskStatusType(scope.row.taskStatus)">
              {{ getTaskStatusText(scope.row.taskStatus) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column
          label="操作"
          width="150"
        >
          <template slot-scope="scope">
            <el-button
              size="mini"
              @click="viewTaskDetails(scope.row)"
            >
              查看详情
            </el-button>
            <el-button
              v-if="scope.row.taskStatus === 'pending'"
              size="mini"
              type="danger"
              @click="cancelTask(scope.row)"
            >
              取消
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>
  </div>
</template>

<script>
import request from '@/utils/request'
import { formatDate } from '@/utils/date'

export default {
  name: 'SmsTaskList',
  
  props: {
    archiveId: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      smsStatistics: {
        total: 0,
        pending: 0,
        processing: 0,
        completed: 0
      },
      smsTaskLoading: false,
      smsTaskList: [],
      pollingTimer: null,
      currentRequest: null,
      isDestroyed: false  // 添加销毁标志
    }
  },

  created() {
    this.isDestroyed = false
    this.loadSmsTaskList()
  },

  beforeDestroy() {
    this.isDestroyed = true
    this.stopPolling()
  },

  deactivated() {
    this.stopPolling()
  },

  methods: {
    formatDateTime(date) {
      return formatDate(date, 'YYYY-MM-DD HH:mm:ss')
    },
    
    // 刷新任务列表
    refreshTaskList() {
      if (!this.isDestroyed) {
        this.loadSmsTaskList()
      }
    },
    
    // 加载短信任务列表
    async loadSmsTaskList() {
      if (!this.archiveId || this.isDestroyed) return
      
      // 如果有正在进行的请求，先取消它
      if (this.currentRequest && typeof this.currentRequest.cancel === 'function') {
        this.currentRequest.cancel()
      }
      
      this.smsTaskLoading = true
      try {
        this.currentRequest = request.get('/sms/tasks', {
          params: {
            archiveId: this.archiveId
          }
        })
        
        const response = await this.currentRequest
        
        // 检查组件是否已销毁
        if (this.isDestroyed) {
          return
        }
        
        if (response.status === '10000') {
          this.smsTaskList = response.data.tasks || []
          this.smsStatistics = response.data.statistics || {
            total: 0,
            pending: 0,
            processing: 0,
            completed: 0
          }
          
          // 如果有处理中的任务，开启轮询
          this.checkAndStartPolling()
        } else {
          if (!this.isDestroyed) {
            this.$message.error(response.message || '获取任务列表失败')
          }
        }
      } catch (error) {
        if (error.message !== 'canceled' && !this.isDestroyed) {  // 忽略取消的请求错误
          console.error('加载短信任务列表失败:', error)
          this.$message.error('加载任务列表失败')
        }
      } finally {
        if (!this.isDestroyed) {
          this.smsTaskLoading = false
        }
        this.currentRequest = null
      }
    },
    
    // 检查是否需要开启轮询
    checkAndStartPolling() {
      if (this.isDestroyed) return
      
      this.stopPolling() // 先停止现有的轮询
      
      // 如果有pending或processing状态的任务，开启轮询
      const hasActiveTask = this.smsTaskList.some(task => 
        task.taskStatus === 'pending' || task.taskStatus === 'processing'
      )
      
      if (hasActiveTask && !this.isDestroyed) {
        this.startPolling()
      }
    },
    
    // 开始轮询
    startPolling() {
      if (this.isDestroyed) return
      
      this.stopPolling() // 确保先停止现有的轮询
      
      this.pollingTimer = setInterval(() => {
        if (!this.smsTaskLoading && !this.isDestroyed) { // 只有在没有加载中且未销毁时才发起新的请求
          this.loadSmsTaskList()
        }
      }, 5000) // 5秒轮询一次
    },
    
    // 停止轮询
    stopPolling() {
      if (this.pollingTimer) {
        clearInterval(this.pollingTimer)
        this.pollingTimer = null
      }
      // 确保取消所有未完成的请求
      if (this.currentRequest && typeof this.currentRequest.cancel === 'function') {
        this.currentRequest.cancel()
        this.currentRequest = null
      }
    },
    
    // 查看任务详情
    viewTaskDetails(task) {
      this.$emit('view-details', task)
    },
    
    // 取消任务
    async cancelTask(task) {
      try {
        await this.$confirm('确定要取消这个发送任务吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        
        const response = await request.post('/sms/task/cancel', {
          taskId: task.taskId
        })
        
        if (response.status === '10000') {
          this.$message.success('任务已取消')
          this.loadSmsTaskList()
          this.$emit('cancel-task', task)
        } else {
          this.$message.error(response.message || '取消任务失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('取消任务失败:', error)
          this.$message.error('取消任务失败')
        }
      }
    },
    
    // 获取任务状态类型
    getTaskStatusType(taskStatus) {
      const statusMap = {
        pending: 'info',
        processing: 'warning', 
        completed: 'success',
        cancelled: 'danger'
      }
      return statusMap[taskStatus] || 'info'
    },
    
    // 获取任务状态文本
    getTaskStatusText(taskStatus) {
      const statusMap = {
        pending: '待处理',
        processing: '处理中',
        completed: '已完成',
        cancelled: '已取消'
      }
      return statusMap[taskStatus] || taskStatus
    }
  }
}
</script>

<style lang="scss" scoped>
.sms-task-list {
  .task-statistics {
    margin-bottom: 20px;
    
    .stat-item {
      text-align: center;
      padding: 20px;
      background-color: #f5f7fa;
      border-radius: 4px;
      
      .stat-label {
        color: #909399;
        font-size: 14px;
        margin-bottom: 10px;
      }
      
      .stat-value {
        color: #303133;
        font-size: 24px;
        font-weight: bold;
      }
    }
  }
}
</style> 